// components/KycDetails.jsx
import React, { useState } from "react";

// ✅ Helper Component defined OUTSIDE to prevent re-render/focus loss
const UploadCard = ({ label, accept, multiple, files, onChange }) => (
  <div className="flex flex-col space-y-2">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 cursor-pointer hover:border-[#0CD974] hover:bg-gray-100 transition-all">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-8 w-8 text-gray-400 mb-2"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M12 12v9m0-9l-4 4m4-4l4 4"
        />
      </svg>
      <span className="text-gray-500 text-sm">
        {files
          ? Array.isArray(files)
            ? `${files.length} files`
            : files.name
          : "Click to upload or drag files"}
      </span>
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={onChange}
        className="hidden"
      />
    </label>
    {files && (
      <ul className="text-xs text-gray-500 space-y-1 max-h-20 overflow-y-auto">
        <li className="truncate text-green-600 font-medium">
          ✓ {Array.isArray(files) ? "Files ready" : files.name}
        </li>
      </ul>
    )}
  </div>
);

export default function KycDetails({
  data,
  update,
  nextStep,
  prevStep, // ✅ Add this
  isLastStep,
  onFinalSubmit,
}) {
  const [submitError, setSubmitError] = useState("");

  const safeNextStep = typeof nextStep === "function" ? nextStep : () => {};
  const safePrevStep = typeof prevStep === "function" ? prevStep : () => {}; // ✅ Add this
  const safeFinalSubmit =
    typeof onFinalSubmit === "function" ? onFinalSubmit : () => {};

  const handleChange = (field, value) => {
    update({ ...data, [field]: value });
  };

  const handleFileChange = (field, e) => {
    const file = e.target.files[0];
    update({ ...data, [field]: file });
  };

  const requiredFields = [
    "aadhaarNumber",
    "bankName",
    "ifscCode",
    "bankAccount",
  ];

  const isFormValid = () => {
    // Check required text fields
    const fieldsValid = requiredFields.every((field) =>
      data?.[field]?.toString().trim(),
    );

    // Check that ALL three documents are uploaded
    const documentsValid = data?.aadhaarFile && data?.panFile && data?.bankFile;

    return fieldsValid && documentsValid;
  };

  const handleSubmit = () => {
    setSubmitError("");

    if (!isFormValid()) {
      // Check what's missing and show specific error
      const missingFields = [];

      if (!data?.aadhaarNumber?.trim()) missingFields.push("Aadhaar Number");
      if (!data?.bankName?.trim()) missingFields.push("Bank Name");
      if (!data?.ifscCode?.trim()) missingFields.push("IFSC Code");
      if (!data?.bankAccount?.trim()) missingFields.push("Bank Account");

      const missingDocs = [];
      if (!data?.aadhaarFile) missingDocs.push("Aadhaar Card");
      if (!data?.panFile) missingDocs.push("PAN Card");
      if (!data?.bankFile) missingDocs.push("Bank Passbook");

      let errorMsg = "";
      if (missingFields.length > 0) {
        errorMsg += `Missing fields: ${missingFields.join(", ")}. `;
      }
      if (missingDocs.length > 0) {
        errorMsg += `Missing documents: ${missingDocs.join(", ")}.`;
      }

      setSubmitError(
        errorMsg ||
          "Please complete all required fields and upload all documents.",
      );
      return;
    }

    if (isLastStep) {
      safeFinalSubmit();
    } else {
      safeNextStep();
    }
  };

  return (
    <div className="space-y-8 mt-4">
      {/* Row 1: Identity Numbers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          {
            label: "Aadhaar Number*",
            field: "aadhaarNumber",
            placeholder: "xxxx xxxx xxxx",
            helper: "12-digit Aadhaar number",
          },
          {
            label: "PAN Number*",
            field: "panNumber",
            placeholder: "ABCDE1234F",
            helper: "10-character PAN number",
          },
        ].map(({ label, field, placeholder, helper }) => (
          <div key={field} className="space-y-2">
            <label className="text-sm pl-4 text-[#666666] font-medium">
              {label}
            </label>
            <input
              value={data?.[field] || ""}
              onChange={(e) => handleChange(field, e.target.value)}
              placeholder={placeholder}
              className="w-full h-12 px-4 rounded-[10px] bg-[#F5F5F5] border border-transparent focus:bg-white focus:border-[#0CD974] outline-none"
            />
            {helper && <p className="text-xs pl-4 text-gray-400">{helper}</p>}
          </div>
        ))}
      </div>

      {/* Row 2: Passport Details */}
      <label className="text-xl text-[#000000] font-medium block border-b pb-2">
        Passport Details{" "}
        <span className="text-sm font-normal text-gray-400">(Optional)</span>
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-sm pl-4 text-[#666666] font-medium">
            Passport Number
          </label>
          <input
            value={data?.passportNumber || ""}
            onChange={(e) => handleChange("passportNumber", e.target.value)}
            placeholder="Enter passport number"
            className="w-full h-12 px-4 rounded-[10px] bg-[#F5F5F5] border border-transparent focus:bg-white focus:border-[#0CD974] outline-none"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm pl-4 text-[#666666] font-medium">
            Passport Expiry Date
          </label>
          <input
            type="date"
            value={data?.passportExpiry || ""}
            onChange={(e) => handleChange("passportExpiry", e.target.value)}
            className="w-full h-12 px-4 rounded-[10px] bg-[#F5F5F5] border border-transparent focus:bg-white focus:border-[#0CD974] outline-none"
          />
        </div>
      </div>

      {/* Row 3: Bank Details */}
      <label className="text-xl text-[#000000] font-medium block border-b pb-2">
        Bank Details
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-sm pl-4 text-[#666666] font-medium">
            Bank Name*
          </label>
          <select
            value={data?.bankName || ""}
            onChange={(e) => handleChange("bankName", e.target.value)}
            className="w-full h-12 px-4 rounded-[10px] bg-[#F5F5F5] border border-transparent focus:bg-white focus:border-[#0CD974] outline-none"
          >
            <option value="">Select Bank</option>
            <option value="SBI">State Bank of India</option>
            <option value="HDFC">HDFC Bank</option>
            <option value="ICICI">ICICI Bank</option>
            <option value="AXIS">Axis Bank</option>
            <option value="KOTAK">Kotak Mahindra Bank</option>
            <option value="PNB">Punjab National Bank</option>
            <option value="BOB">Bank of Baroda</option>
            <option value="CANARA">Canara Bank</option>
            <option value="UNION">Union Bank of India</option>
            <option value="BOI">Bank of India</option>
            <option value="INDIAN">Indian Bank</option>
            <option value="CENTRAL">Central Bank of India</option>
            <option value="IOB">Indian Overseas Bank</option>
            <option value="UCO">UCO Bank</option>
            <option value="BOM">Bank of Maharashtra</option>
            <option value="PSB">Punjab & Sind Bank</option>
            <option value="IDFC">IDFC First Bank</option>
            <option value="INDUS">IndusInd Bank</option>
            <option value="YES">Yes Bank</option>
            <option value="FEDERAL">Federal Bank</option>
            <option value="RBL">RBL Bank</option>
            <option value="BANDHAN">Bandhan Bank</option>
            <option value="IDBI">IDBI Bank</option>
            <option value="SIB">South Indian Bank</option>
            <option value="KARNATAKA">Karnataka Bank</option>
            <option value="KVB">Karur Vysya Bank</option>
            <option value="CUB">City Union Bank</option>
            <option value="TMB">Tamilnad Mercantile Bank</option>
            <option value="CSB">CSB Bank</option>
            <option value="DCB">DCB Bank</option>
            <option value="JKB">Jammu & Kashmir Bank</option>
            <option value="Dhanlaxmi">Dhanlaxmi Bank</option>
            <option value="Nainital">Nainital Bank</option>
            <option value="CITI">Citibank</option>
            <option value="HSBC">HSBC Bank</option>
            <option value="SCB">Standard Chartered Bank</option>
            <option value="DB">Deutsche Bank</option>
            <option value="BARCLAYS">Barclays Bank</option>
            <option value="DBS">DBS Bank</option>
            <option value="BOA">Bank of America</option>
            <option value="JPM">JP Morgan Chase Bank</option>
            <option value="BNPP">BNP Paribas Bank</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm pl-4 text-[#666666] font-medium">
            IFSC Code*
          </label>
          <input
            value={data?.ifscCode || ""}
            onChange={(e) => handleChange("ifscCode", e.target.value)}
            placeholder="Enter IFSC code"
            className="w-full h-12 px-4 rounded-[10px] bg-[#F5F5F5] border border-transparent focus:bg-white focus:border-[#0CD974] outline-none"
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <label className="text-sm pl-4 text-[#666666] font-medium">
            Bank Account Number*
          </label>
          <input
            value={data?.bankAccount || ""}
            onChange={(e) => handleChange("bankAccount", e.target.value)}
            placeholder="Enter account number"
            className="w-full h-12 px-4 rounded-[10px] bg-[#F5F5F5] border border-transparent focus:bg-white focus:border-[#0CD974] outline-none"
          />
        </div>
      </div>

      {/* Row 4: File Uploads */}
      <div className="space-y-4 mt-6">
        <label className="text-xl font-medium text-[#000000] block border-b pb-2">
          Upload Documents
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <UploadCard
            label="Aadhaar Card (Front/Back)*" // ✅ Added asterisk
            accept=".pdf,image/*"
            multiple={false}
            files={data?.aadhaarFile}
            onChange={(e) => handleFileChange("aadhaarFile", e)}
          />
          <UploadCard
            label="PAN Card (Front)*" // ✅ Added asterisk
            accept=".pdf,image/*"
            multiple={false}
            files={data?.panFile}
            onChange={(e) => handleFileChange("panFile", e)}
          />
          <UploadCard
            label="Bank Passbook (1st Page)*" // ✅ Added asterisk
            accept=".pdf,image/*"
            multiple={false}
            files={data?.bankFile}
            onChange={(e) => handleFileChange("bankFile", e)}
          />
        </div>
        <p className="text-xs text-gray-400">
          Max file size: 5MB each. Formats: PDF, JPG, PNG
        </p>
      </div>

      <div className="flex justify-between mt-6 pt-6 border-t border-gray-100">
        <button
          onClick={prevStep}
          className="px-8 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
        >
          Previous
        </button>

        <button
          onClick={handleSubmit}
          className="px-8 py-3 rounded-lg bg-[#0CD974] text-white font-medium hover:bg-[#0bb963] transition-colors shadow-md"
        >
          {isLastStep ? "Submit All" : "Save & Continue"}
        </button>
      </div>
      {submitError && (
        <div className="fixed bottom-6 left-6 z-50 max-w-sm bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg">
          <p className="text-sm font-medium">{submitError}</p>
        </div>
      )}
    </div>
  );
}
